import { createSlice } from '@reduxjs/toolkit';
import AbstractSliceHandler from 'modules/shared/stores/abstract-slice-handler';
import LocalEvent from 'utils/local-event';
import Log from 'utils/log';
import { getLoggedInMsisdn } from 'utils/contact-util';
import { addSnackbar } from 'utils/dialogs-manager';
import SnackbarTypes from 'constants/snackbar-types';
import t from 'utils/translate';

const initialState = {
    guestInvitationPopupOn: false,
    validationRulesLoaded: false,
    guestProfile: {
        ForbiddenCountryCodes: [],
        ForbiddenDomains: [],
        AllowedDomains: [],
    },
};

class GuestInvitationSliceHandler extends AbstractSliceHandler {
    static getInstance() {
        if (!GuestInvitationSliceHandler.instance) {
            GuestInvitationSliceHandler.instance = new GuestInvitationSliceHandler();
        }

        return GuestInvitationSliceHandler.instance;
    }

    constructor() {
        super('guestInvitationSlice');

        LocalEvent.setListener(LocalEvent.eventTypes.SDK_CLIENT_AUTHENTICATED, () => {
            this.loadGuestProfile();
        });

        const stringToArray = (input) => (input !== '' ? input?.split(',') : []);

        this.slice = createSlice({
            name: this.sliceName,
            initialState,
            reducers: {
                setGuestInvitationPopupOn(state, action) {
                    state.guestInvitationPopupOn = action.payload;
                },
                setGuestProfile(state, action) {
                    action.payload.ForbiddenCountryCodes = stringToArray(action.payload.ForbiddenCountryCodes);
                    action.payload.ForbiddenDomains = stringToArray(action.payload.ForbiddenDomains);
                    action.payload.AllowedDomains = stringToArray(action.payload.AllowedDomains);

                    state.guestProfile = action.payload;
                },
                setValidationRulesLoaded(state, action) {
                    state.validationRulesLoaded = action.payload;
                },
            },
        });
    }

    openGuestInvitationModal = () => {
        this.dispatch('setGuestInvitationPopupOn', true);
    };

    closeGuestInvitationModal = () => {
        this.dispatch('setGuestInvitationPopupOn', false);
    };

    loadGuestProfile = () =>
        new Promise((resolve) => {
            webchatSDK.STWAPI.getGuestProfile().then(
                (response) => {
                    this.dispatch('setGuestProfile', response.data.results);
                    this.dispatch('setValidationRulesLoaded', true);
                    resolve(true);
                },
                (err) => {
                    this.treatSdkError('getGuestProfile', err);
                    resolve(false);
                },
            );
        });

    sendInvitation = (guestData) =>
        webchatSDK.STWAPI.inviteGuestMember(guestData, getLoggedInMsisdn())
            .then(() => {
                Log.debug('GUEST INVITATION STORE', `Guest invitation sent successfully`);
                addSnackbar({
                    type: SnackbarTypes.SUCCESS,
                    title: t('js_all_guest_invitation_sent_title'),
                    message: t('js_all_guest_invitation_sent_successfully'),
                });
            })
            .catch((err) => {
                Log.error(
                    'GUEST INVITATION STORE',
                    `There was an error sending the guest invitation: ${JSON.stringify(err)}`,
                );

                let errMessage = t('js_all_guest_invitation_sent_error');
                if (err.response?.status === 409) {
                    errMessage = t('js_all_guest_invitation_sent_error_existing_user');
                }
                addSnackbar({
                    type: SnackbarTypes.ERROR,
                    title: t('js_all_guest_invitation_sent_title'),
                    message: errMessage,
                });
            });
}

export const guestInvitationSliceHandler = GuestInvitationSliceHandler.getInstance();
export const selectGuestProfile = (state) => state.guestInvitationSlice.guestProfile;
export const selectGuestInvitationPopupOn = (state) => state.guestInvitationSlice.guestInvitationPopupOn;
export const selectValidationRulesLoaded = (state) => state.guestInvitationSlice.validationRulesLoaded;
export const { openGuestInvitationModal, closeGuestInvitationModal, sendInvitation } = guestInvitationSliceHandler;
